import { ref, watch } from 'vue';

import { curry, clone } from 'ramda';
import { isFunction } from './types';

/**
 * @description This function binds the value and lazyValue of the component together
 * @param {string} valueProp Indicates which field in props to bind.
 * @param {function} emit Allows to emit an event
 * @param {object} props Properties of component
 * @param {string,array,object} defaultValue Default value of the field to bind (optional)
 * @returns {{lazyValue: function, updateValue: updateValue}} lazyValue: Updated value, updateValue: Update function
 * @example{
 *   const props ={value:'someValue'}
 *   const {lazyValue, updateValue} = useTwoWayBinding('value',emit,props,[]);
 *
 *   return {
 *     lazyValue: 'someValue'
 *     updateValue: A fn that manually binds lazyValue or any field to value
 *   }
 *
 * }
 */
export const useTwoWayBinding = (
  valueProp,
  emit,
  props,
  defaultValue = undefined,
) => {
  if (!isFunction(emit)) {
    throw new Error('Invalid emit function!');
  }

  const getDefaultValue = () => {
    if (defaultValue) {
      return (isFunction(defaultValue) && defaultValue.apply(null, [props, valueProp])) || defaultValue;
    }

    if (typeof defaultValue === 'undefined') {
      return clone(props[valueProp]);
    }

    return defaultValue;
  };

  const lazyValue = ref(getDefaultValue());

  watch(
    () => props[valueProp],
    newValue => {
      lazyValue.value = newValue;
    }, { deep: true },
  );

  const updateValue = newValue => {
    const event = valueProp === 'value' ? 'input' : `update:${valueProp}`;
    emit(event, newValue);
  };

  return {
    lazyValue,
    updateValue,
  };
};

/**
 * @description This function automatically binds the value and lazyValue of the component together
 * @param {string} valueProp Indicates which field in props to bind.
 * @param {function} emit Allows to emit an event
 * @param {object} props Properties of component
 * @param {string,array,object} defaultValue Default value of the field to bind (optional)
 * @returns {{lazyValue: function, updateValue: updateValue}} lazyValue: Updated value, updateValue: Update function
 * @example{
 *   const props ={value:'someValue'}
 *   const {lazyValue, updateValue} = useTwoWayBinding('value',emit,props,[]);
 *
 *   return {
 *     lazyValue: 'someValue'
 *     updateValue: A fn that manually binds lazyValue or any field to value
 *   }
 *
 * }
 */
export const useTwoWayAutoBinding = (
  valueProp,
  emit,
  props,
  defaultValue = undefined,
) => {
  const { lazyValue, updateValue } = useTwoWayBinding(
    valueProp,
    emit,
    props,
    defaultValue,
  );

  watch(
    () => lazyValue.value,
    newValue => {
      updateValue(newValue);
    }, { deep: true },
  );

  return {
    lazyValue,
    updateValue,
  };
};

/**
 * @function
 * @param {function} emit Emit fn from setupContext
 * @param {object} props props data of component
 * */
export const useModelBinding = curry(useTwoWayBinding)('value');

/**
 * @function
 * @param {function} emit Emit fn from setupContext
 * @param {object} props props data of component
 * */
export const useModelAutoBinding = curry(useTwoWayAutoBinding)('value');
